import { IngredientProps } from '@/interfaces/models/ingredient';
import stringHelper from '@/utils/stringHelper';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '@/store';
import menuApi from '@/api/menu';
import Ingredient from '@/models/ingredient';
import Product from '@/models/product';
import { ProductProps } from '@/interfaces/models/product';

const namespaced: boolean = true;

type MenuState = {
  categories: any[];
  defaultCategory: any;
  ingredients: any[];
  isFetchingCategories: boolean;
  isCategoriesWasFetch: boolean;
  isFetchingIngredients: boolean;
  isIngredientsWasFetch: boolean;
};

const getDefaultState = (): MenuState => {
  return {
    categories: [],
    defaultCategory: {
      products: []
    },
    ingredients: [],
    isFetchingCategories: false,
    isCategoriesWasFetch: false,
    isFetchingIngredients: false,
    isIngredientsWasFetch: false
  };
};

const state: MenuState = getDefaultState();

const getters: GetterTree<MenuState, RootState> = {
  favoriteProducts() {
    const products = [];

    for (let index = 0; index < state.categories.length; index++) {
      products.push(...state.categories[index].products.filter(({ isFavorite }: Product) => isFavorite));
    }

    return products;
  }
};

const actions: ActionTree<MenuState, RootState> = {
  async fetchCategories({ commit, state }: ActionContext<MenuState, RootState>) {
    if (!state.isCategoriesWasFetch && !state.isFetchingCategories) {
      commit('setIsFetchingCategories', true);

      return menuApi.fetchCategories()
        .then(async ({ categories, defaultCategory }: any) => {
          commit('setCategories', categories);
          commit('setDefaultCategory', defaultCategory);
          commit('setIsFetchingCategories', false);
          commit('setIsCategoriesWasFetch', true);
        });
    }
  },
  async fetchCategoriesForPos({ commit, state }: ActionContext<MenuState, RootState>) {
    if (!state.isCategoriesWasFetch && !state.isFetchingCategories) {
      commit('setIsFetchingCategories', true);

      return menuApi.fetchCategoriesForPos()
        .then(async ({ categories, defaultCategory }: any) => {
          commit('setCategories', categories);
          commit('setDefaultCategory', defaultCategory);
          commit('setIsFetchingCategories', false);
          commit('setIsCategoriesWasFetch', true);
        });
    }
  },
  async fetchIngredients({ commit, state }: ActionContext<MenuState, RootState>) {
    if (!state.isIngredientsWasFetch && !state.isFetchingIngredients) {
      commit('setIsFetchingIngredients', true);

      return menuApi.fetchIngredients()
        .then(async (ingredients: any) => {
          commit('setIngredients', ingredients);
          commit('setIsFetchingIngredients', false);
          commit('setIsIngredientsWasFetch', true);
        });
    }
  },
  async createCategory({ dispatch }: ActionContext<MenuState, RootState>, categoryName: string) {
    const newCategory = await menuApi.createCategory(categoryName);

    dispatch('socketCreateCategory', newCategory, { root: true });
  },
  async renameCategory({ dispatch }: ActionContext<MenuState, RootState>, payload: { id: string, name: string }) {
    menuApi.renameCategory(payload);

    return dispatch('socketRenameCategory', payload, { root: true });
  },
  async removeCategoryWithoutProducts({ dispatch }: ActionContext<MenuState, RootState>, categoryId: string) {
    menuApi.removeCategoryWithoutProducts(categoryId);

    return dispatch('socketRemoveCategory', { id: categoryId }, { root: true });
  },
  async removeCategoryWithProducts({ dispatch }: ActionContext<MenuState, RootState>, categoryId: string) {
    menuApi.removeCategoryWithProducts(categoryId);

    return dispatch('socketPurgeCategory', { id: categoryId }, { root: true });
  },
  async importProduct({ dispatch }: ActionContext<MenuState, RootState>, payload: any) {
    const productProps = await menuApi.importProduct(payload);

    return dispatch('socketCreateProduct', productProps, { root: true });
  },
  async sortCategories({ commit, state }: ActionContext<MenuState, RootState>, data: { categoryId: string, position: number }) {
    menuApi.sortCategories(data);

    commit('sortCategories', data);
  },
  async toggleCategoryVisibilityInPos({ dispatch }: ActionContext<MenuState, RootState>, { id }: any) {
    menuApi.toggleCategoryVisibilityInPos(id);

    dispatch('socketToggleCategoryVisibilityInPos', { id }, { root: true });
  },
  async toggleCategoryVisibilityInWidget({ dispatch }: ActionContext<MenuState, RootState>, { id, isShowInWidget }: any) {
    menuApi.toggleCategoryVisibilityInWidget(id);

    dispatch('socketToggleCategoryVisibilityInWidget', { id }, { root: true });
  },
  async sortProducts({ commit }: ActionContext<MenuState, RootState>, data: any) {
    menuApi.sortProducts(data);

    commit('sortProducts', data);
  },
  async createProduct({ dispatch }: ActionContext<MenuState, RootState>, product: Product) {
    const productProps = await menuApi.createProduct(product);

    return dispatch('socketCreateProduct', productProps, { root: true });
  },
  async editProduct({ dispatch }: ActionContext<MenuState, RootState>, payload: any) {
    const editedFields = await menuApi.editProduct(payload);

    return dispatch('socketEditProduct', editedFields, { root: true });
  },
  async removeProduct({ dispatch }: ActionContext<MenuState, RootState>, product: Product) {
    const ids = await menuApi.removeProduct(product.id);

    return dispatch('socketRemoveProduct', ids, { root: true });
  },
  async toggleProductVisibilityInPos({ dispatch }: ActionContext<MenuState, RootState>, product: Product) {
    if (product.isShowInPos) {
      menuApi.hideProductInPos(product.id);
    } else {
      menuApi.showProductInPos(product.id);
    }

    dispatch('socketToggleProductVisibilityInPos', product, { root: true });
  },
  async toggleProductVisibilityInWidget({ dispatch }: ActionContext<MenuState, RootState>, product: Product) {
    if (product.isShow) {
      menuApi.hideProductInWidget(product.id);
    } else {
      menuApi.showProductInWidget(product.id);
    }

    dispatch('socketToggleProductVisibility', product, { root: true });
  },
  async toggleIsFavoriteProduct({ dispatch }: ActionContext<MenuState, RootState>, product: Product) {
    menuApi.toggleProductFavorite(product.id);

    await dispatch('socketToggleIsFavoriteProduct', product, { root: true });
  },
  async editIngredient({ dispatch }: ActionContext<MenuState, RootState>, payload: any) {
    const editedFields = await menuApi.editIngredient(payload);

    return dispatch('socketEditIngredient', editedFields, { root: true });
  },
  async removeIngredient({ dispatch }: ActionContext<MenuState, RootState>, ingredient: Ingredient) {
    const ids = await menuApi.removeIngredient(ingredient.id);

    return dispatch('socketRemoveIngredient', ids, { root: true });
  },
  async getProductsByProductTitle({ state }: ActionContext<MenuState, RootState>, searchString: string) {
    const searchProducts = [];

    searchString = searchString.toLocaleLowerCase();

    for (let index = 0; index < state.categories.length; index++) {
      const products = state.categories[index].products.filter(({ isShow, title }: Product) => {
        return isShow && title.toLocaleLowerCase().includes(searchString);
      });

      searchProducts.push(...products);
    }

    return searchProducts;
  },
  async getProductsByTitleAndPlaceIds({ state }: ActionContext<MenuState, RootState>, payload: {title: string, placeIds: string[], addedProductIds: string[]}) {
    return menuApi.getProductsByTitleAndPlaceIds(payload.title, payload.placeIds, payload.addedProductIds);
  }
};

const mutations: MutationTree<MenuState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setCategories(state, categories: any[]) {
    state.categories = categories;
  },
  setDefaultCategory(state, defaultCategory: Product[]) {
    state.defaultCategory = defaultCategory;
  },
  setIngredients(state, ingredients: any[]) {
    state.ingredients = ingredients;
  },
  createCategory(state, newCategory: any) {
    state.categories.push(newCategory);
  },
  renameCategory(state, categoryProps: { id: string, name: string }) {
    const category = state.categories.find(({ id }: any) => id === categoryProps.id);

    if (category !== undefined) {
      category.name = categoryProps.name;
    }
  },
  removeCategoryWithoutProducts(state, categoryId: string) {
    const categoryIndex = state.categories.findIndex(({ id }: any) => id === categoryId);

    if (categoryIndex !== -1) {
      const products = state.categories[categoryIndex].products;

      for (let i = 0; i < products.length; i++) {
        products[i].category = null;
      }

      state.categories.splice(categoryIndex, 1);
      state.defaultCategory.products.push(...products);
    }
  },
  removeCategoryWithProducts(state, categoryId: string) {
    const categoryIndex = state.categories.findIndex(({ id }: any) => id === categoryId);

    if (categoryIndex !== -1) {
      state.categories.splice(categoryIndex, 1);
    }
  },
  sortCategories(state, { categoryId, position }: any) {
    const oldPosition = state.categories.findIndex(({ id }: any) => id === categoryId);
    const [draggedCategory] = state.categories.splice(oldPosition, 1);

    state.categories.splice(position, 0, draggedCategory)

    for (let index = 0; index < state.categories.length; index++) {
      state.categories[index].priority = index + 1;
    }
  },
  toggleCategoryVisibilityInPos(state, categoryId: string) {
    const category = state.categories.find(({ id }: any): boolean => id === categoryId);

    if (category) {
      const isSomeProductShowInPos = category.products.some((product: Product): boolean => product.isShowInPos);

      if (isSomeProductShowInPos) {
        for (const product of category.products) {
          product.hideInPos();
        }
      } else {
        for (const product of category.products) {
          product.showInPos();
        }
      }
    }
  },
  toggleCategoryVisibilityInWidget(state, categoryId: string) {
    const category = state.categories.find(({ id }: any): boolean => id === categoryId);

    if (category) {
      const isSomeProductShowInWidget = category.products.some((product: Product): boolean => product.isShow);

      if (isSomeProductShowInWidget) {
        for (const product of category.products) {
          product.hideInWidget();
        }
      } else {
        for (const product of category.products) {
          product.showInWidget();
        }
      }
    }
  },
  sortProducts(state, data: any) {
    const oldCategory: any = null === data.oldCategoryId
      ? state.defaultCategory
      : state.categories.find(({ id }): any => id === data.oldCategoryId);
    const category: any = null === data.categoryId
      ? state.defaultCategory
      : state.categories.find(({ id }): any => id === data.categoryId);
    const [product] = oldCategory.products.splice(data.oldPosition, 1);

    category.products.splice(data.position, 0, product);
    product.category = {
      id: category.id,
      name: category.name,
    }

    for (let index = 0; index < category.products.length; index++) {
      category.products[index].priority = index + 1;
    }
  },
  createProduct(state, productProps: ProductProps) {
    const product = Product.createFromRequest(productProps);

    let category = state.defaultCategory;

    if (productProps.category !== null) {
      category = state.categories.find((category: any) => category.id === productProps.category.id);
    }

    if (category !== undefined) {
      category.products.push(product);
    }
  },
  editProduct(state, productProps: ProductProps) {
    let category = state.defaultCategory;

    if (productProps.category !== null) {
      category = state.categories.find((category: any) => category.id === productProps.category.id);
    }

    if (category !== undefined) {
      const product = category.products.find(({ id }: Product) => id === productProps.oldId);

      if (product !== undefined) {
        product.mergeWithNewFields(productProps);
      }
    }
  },
  removeProduct(state, productProps: ProductProps) {
    let category = state.defaultCategory;

    if (productProps.category !== null) {
      category = state.categories.find((category: any) => category.id === productProps.category.id);
    }

    if (category !== undefined) {
      const productIndex = category.products.findIndex(({ id }: Product) => id === productProps.id);

      if (productIndex !== -1) {
        category.products.splice(productIndex, 1);
      }
    }
  },
  toggleIsFavoriteProduct(state, productProps: ProductProps) {
    let category = state.defaultCategory;

    if (productProps.category !== null) {
      category = state.categories.find((category: any) => category.id === productProps.category.id);
    }

    if (category !== undefined) {
      const product: Product | undefined = category.products.find(({ id }: Product) => id === productProps.id);

      if (product !== undefined) {
        product.toggleIsFavorite();
      }
    }
  },
  toggleProductVisibility(state, productProps: ProductProps) {
    let category = state.defaultCategory;

    if (productProps.category !== null) {
      category = state.categories.find((category: any) => category.id === productProps.category.id);
    }

    if (category !== undefined) {
      const product: Product | undefined = category.products.find(({ id }: Product) => id === productProps.id);

      if (product !== undefined) {
        product.toggleIsShow();
      }
    }
  },
  toggleProductVisibilityInPos(state, productProps: ProductProps) {
    let category = state.defaultCategory;

    if (productProps.category !== null) {
      category = state.categories.find((category: any) => category.id === productProps.category.id);
    }

    if (category !== undefined) {
      const product: Product | undefined = category.products.find(({ id }: Product) => id === productProps.id);

      if (product !== undefined) {
        product.toggleIsShowInPos();
      }
    }
  },
  createIngredient(state, ingredientProps: IngredientProps) {
    const ingredient = Ingredient.createFromRequest(ingredientProps);

    state.ingredients.push(ingredient);

    state.ingredients.sort((firstIngredient: Ingredient, secondIngredient: Ingredient) => {
      return stringHelper.sortStrings(
        firstIngredient.name,
        secondIngredient.name
      );
    })
  },
  editIngredient(state, ingredientProps: IngredientProps) {
    const ingredient = state.ingredients.find(({ id }: Ingredient) => id === ingredientProps.oldId);

    if (ingredient !== undefined) {
      ingredient.mergeWithNewFields(ingredientProps);
    }

    for (const category of state.categories) {
      for (const product of category.products) {
        for (const ingredient of product.ingredients) {
          if (ingredient.id === ingredientProps.oldId) {
            ingredient.mergeWithNewFields(ingredientProps);
          }
        }
      }
    }

    for (const product of state.defaultCategory.products) {
      for (const ingredient of product.ingredients) {
        if (ingredient.id === ingredientProps.oldId) {
          ingredient.mergeWithNewFields(ingredientProps);
        }
      }
    }
  },
  removeIngredient(state, ingredientId: string) {
    const ingredientIndex = state.ingredients.findIndex(({ id }: Ingredient) => id === ingredientId);

    if (ingredientIndex !== -1) {
      state.ingredients.splice(ingredientIndex, 1);
    }

    for (const category of state.categories) {
      for (const product of category.products) {
        for (const group of product.groups) {
          for (let index = 0; index < group.ingredients.length; index++) {
            if (group.ingredients[index].id === ingredientId) {
              group.ingredients.splice(index, 1);

              index--;
            }
          }
        }
      }
    }

    for (const product of state.defaultCategory.products) {
      for (const group of product.groups) {
        for (let index = 0; index < group.ingredients.length; index++) {
          if (group.ingredients[index].id === ingredientId) {
            group.ingredients.splice(index, 1);

            index--;
          }
        }
      }
    }
  },
  setIsFetchingCategories(state, isFetchingCategories) {
    state.isFetchingCategories = isFetchingCategories;
  },
  setIsCategoriesWasFetch(state, isCategoriesWasFetch) {
    state.isCategoriesWasFetch = isCategoriesWasFetch;
  },
  setIsFetchingIngredients(state, isFetchingIngredients) {
    state.isFetchingIngredients = isFetchingIngredients;
  },
  setIsIngredientsWasFetch(state, isIngredientsWasFetch) {
    state.isIngredientsWasFetch = isIngredientsWasFetch;
  }
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
